import moment from "moment";
import numeral from "numeral";
import React, {useEffect, useState} from "react";
import ReactTable from "react-table";
import "react-table/react-table.css";
import {
    checkExistBankCostRate as checkExistBankCostRateApi,
    createBankCostRate as createBankCostRateApi,
    getBankCostRate as getBankCostRateApi,
} from "../../api/IrsClient";
import {COUNTRY, KEYCLOAK_ROLES} from "../../constant";
import Modal from "../common/Modal";
import ModalError from "../common/ModalError";
import ConfigurationForm from "./ConfigurationForm";
import SearchForm from "./SearchForm";
import ModalNotFound from "../common/ModalNotFound";

const TRANSACTION_DATE_FORMAT = "DD-MM-YYYY";
const DISPLAY_TRANSACTION_DATE_FORMAT = "DD/MM/YYYY";

function ExchangeRateConfiguration({ userRoles }) {
  const [showPagination, setShowPagination] = useState(true);
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0);
  const [pages, setPages] = useState(1);
  const [bankCostRate, setBankCostRate] = useState([]);
  const [filter, setFilter] = useState(null);
  const [isWarningExistingBankCostRate, setIsWarningExistingBankCostRate] =
    useState(false);
  const [bankCostRateConfiguration, setBankCostRateConfiguration] =
    useState(null);
  const [isLoading, setLoading] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [notFound, setNotfound] = useState(null);

  useEffect(() => {
    if (!isLoading && !showPagination) {
      setShowPagination(true);
    }
  }, [isLoading, showPagination]);

  async function getBankCostRate(
    newPage = page,
    newPageSize = pageSize,
    newValues = filter
  ) {
    try {
      let values = newValues;
      if (!values) return;
      values = {
        ...values,
        page_no: newPage + 1,
        item_per_page: newPageSize,
      };

      setLoading(true);
      setFilter(values); // memorize filter
      const {
        data: {
          status,
          data,
        },
      } = await getBankCostRateApi(values);
      if (data.bank_cost_rate_list == null ) {
        reset();
        setNotfound({ title: "" });
        throw Error(status.message);
      }
      setPages(Math.ceil(data.total / data.total_per_page));
      setBankCostRate(data.bank_cost_rate_list);
      setShowPagination(false);
    } catch (error) {
      console.log("Error while call init()", error);
    } finally {
      setLoading(false);
    }
  }

  async function createBankCostRate(newValues = null) {
    try {
      const values = newValues || bankCostRateConfiguration;
      setLoading(true);
      setSubmitting(true);
      const body = {
        amount: parseFloat(values.amount),
        transaction_date: moment(values.date).format(TRANSACTION_DATE_FORMAT),
        country_code: values.countryCode,
      };
      const {
        data: { status },
      } = await createBankCostRateApi(body);
      if (status.code !== "0") {
        setError({ title: status.message });
        throw Error(status.message);
      }
      getBankCostRate();
    } catch (error) {
      console.log("Error while call createBankCostRate()", error);
    } finally {
      setIsWarningExistingBankCostRate(false);
      setBankCostRateConfiguration(null);
      setTimeout(() => {
        setSubmitting(false);
      }, 500);
      setLoading(false);
    }
  }

  async function checkExistBankCostRate(values) {
    try {
      setLoading(true);
      setBankCostRateConfiguration(values);
      const date = moment(values.date).format(TRANSACTION_DATE_FORMAT);
      const countryCode = values.countryCode;
      const {
        data: { status, data },
      } = await checkExistBankCostRateApi(date, countryCode);
      if (status.code !== "0") {
        setBankCostRateConfiguration(null);
        setError({ title: status.message });
        throw Error(status.message);
      }
      if (data.amount == null) {
        createBankCostRate(values);
      }
      else {
        setIsWarningExistingBankCostRate(true);
      }
    } catch (error) {
      console.log("Error while call createBankCostRate()", error);
    } finally {
      setLoading(false);
    }
  }

  function handlePageChange(newPage) {
    setPage(newPage);
    getBankCostRate(newPage);
  }

  function handlePageSizeChange(newPageSize) {
    setPage(0);
    setPageSize(newPageSize);
    getBankCostRate(0, newPageSize);
  }

  function reset() {
    setPage(0);
    setPages(1);
    setPageSize(25);
    setBankCostRate([]);
    setShowPagination(false);
  }

  function prepareColumns() {
    return [
      {
        Header: "Country",
        accessor: "country_name",
        Cell: ({ original }) => {
          const country = COUNTRY.find((x) => x.code === original.country_code);
          return country.name;
        },
      },
      {
        Header: "Currency Unit",
        Cell: ({ original }) => {
          const country = COUNTRY.find((x) => x.code === original.country_code);
          return country.currency;
        },
      },
      {
        Header: "Transaction Date",
        accessor: "transaction_date",
        Cell: ({ value }) => moment(value, "DD-MM-YYYY").format("DD-MM-YYYY"),
      },
      {
        Header: "Bank Cost Rate",
        accessor: "amount",
        Cell: ({ value }) => numeral(value).format("0,0.00000000"),
      },
      {
        Header: "Last Update Date",
        accessor: "create_date",
        Cell: ({ value }) =>
          moment(value, "DD-MM-YYYY HH:mm:ss").format("DD-MM-YYYY HH:mm:ss"),
      },
      {
        Header: "Update By",
        accessor: "create_by",
      },
    ];
  }

  return (
    <>
      <ModalNotFound
        message={"Bank cost rate not found."}
        isOpen={!!notFound}
        description={notFound?.description ?? ""}
        onRequestClose={() => setNotfound(null)}
      />
      <ModalError
        message={error?.title ?? "System Error"}
        isOpen={!!error}
        description={error?.description ?? ""}
        onRequestClose={() => setError(null)}
      />
      <div className="container">
        {userRoles.includes(KEYCLOAK_ROLES.API_SUBMIT_BANK_COST_RATE) && (
          <div className="card card-secondary">
            <div className="card-header">
              <h5>Remittance Configuration - Bank Cost Rate</h5>
            </div>
            <ConfigurationForm
              isLoading={isLoading}
              isSubmitting={isSubmitting}
              history={bankCostRate}
              submit={checkExistBankCostRate}
            />
          </div>
        )}
        {userRoles.includes(KEYCLOAK_ROLES.VIEW_BANK_COST_RATE_CONF) && (
          <div className="card card-secondary mt-2">
            <div className="card-header">
              <h5>Remittance Rates History</h5>
            </div>
            <div className="p-3">
              <div className="mt-3 mb-5">
                <SearchForm
                  isLoading={isLoading}
                  search={(values) => getBankCostRate(page, pageSize, values)}
                  clearBankCostRate={reset}
                />
              </div>
              <ReactTable
                className="-highlight report"
                columns={prepareColumns()}
                data={bankCostRate}
                loading={isLoading}
                manual
                minRows={5}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                page={page}
                pages={pages}
                pageSize={pageSize}
                pageSizeOptions={[25, 50, 100]}
                showPagination={showPagination}
                showPaginationTop
              />
            </div>
          </div>
        )}
      </div>

      <Modal
        width={400}
        isOpen={isWarningExistingBankCostRate}
        onCancel={() => setIsWarningExistingBankCostRate(false)}
      >
        <>
          <div className="custom-modal-content">
            <p className="custom-modal-message text-center">
              The Transaction Date{" "}
              {bankCostRateConfiguration && (
                <strong>
                  {moment(bankCostRateConfiguration.date).format(
                    DISPLAY_TRANSACTION_DATE_FORMAT
                  )}
                </strong>
              )}{" "}
              already has the Bank Cost Rate.
              <br />
              Do you want to change it to{" "}
              <strong>
                {bankCostRateConfiguration &&
                  numeral(bankCostRateConfiguration.amount).format(
                    "0,0.00000000"
                  )}
              </strong>
              ?
            </p>
          </div>
          <div className="custom-modal-action">
            <button data-testid="confirm-bank-cost-rate"
              className="btn btn-warning"
              type="submit"
              onClick={() => createBankCostRate()}
              disabled={isLoading}
            >
              Confirm
            </button>

            <button data-testid="cancel-bank-cost-rate"
              className="btn btn-secondary"
              type="submit"
              onClick={() => setIsWarningExistingBankCostRate(false)}
              disabled={isLoading}
            >
              Cancel
            </button>
          </div>
        </>
      </Modal>
    </>
  );
}

export default ExchangeRateConfiguration;
